/**
 * GoogleSubmarketLayer.tsx
 *
 * Google Maps polygon layer for displaying submarket boundaries
 * with score-based choropleth coloring.
 *
 * Features:
 * - Renders GeoJSON polygons as Google Maps Polygon components
 * - Score-based fill colors using the scoring color scale
 * - Hover/click interactivity with InfoWindow tooltips
 * - Highlighted submarket support
 *
 * Usage:
 *   <GoogleSubmarketLayer
 *     onSubmarketClick={(submarket) => handleClick(submarket)}
 *     onSubmarketHover={(submarket) => handleHover(submarket)}
 *     highlightedSubmarket={selectedId}
 *   />
 */

import type { Polygon as GeoJsonPolygon, MultiPolygon as GeoJsonMultiPolygon, Position as GeoJsonPosition } from 'geojson';
import { useState, useCallback, useMemo } from 'react';
import { Polygon as GooglePolygon, InfoWindow } from '@react-google-maps/api';
import { useSubmarketBoundaries } from '../../services/api';
import {
  useSubmarketScores,
  getScoreValue,
  getScoreColor,
  getScoreLabel,
  type SubmarketScore,
} from '../../hooks/useSubmarketScores';
import { useSubmarketStore } from '../../store/useSubmarketStore';
import { useActiveMarket } from '../../store/useMarketStore';

/**
 * Extended properties for map features including all score data.
 */
export interface SubmarketProperties {
  submarket_id: string;
  name: string;
  cagr: number;
  rank: number;
  score: number;
  composite_score: number;
  rent_gap_score: number;
  mobi_score: number;
  momentum_score: number;
  vacancy_score: number;
  valuation_score: number;
}

interface GoogleSubmarketLayerProps {
  /** Callback when a submarket is clicked */
  onSubmarketClick?: (submarket: SubmarketProperties) => void;
  /** Callback when hovering over a submarket */
  onSubmarketHover?: (submarket: SubmarketProperties | null) => void;
  /** Submarket ID to highlight */
  highlightedSubmarket?: string | null;
}

/**
 * Convert GeoJSON coordinates to Google Maps LatLngLiteral array.
 * GeoJSON uses [lng, lat], Google Maps uses {lat, lng}.
 */
function geoJsonRingToGooglePath(
  ring: GeoJsonPosition[]
): google.maps.LatLngLiteral[] {
  return ring.map(([lng, lat]) => ({ lat, lng }));
}

/**
 * Convert GeoJSON Polygon or MultiPolygon to Google Maps paths.
 * Returns array of paths for use with Google Maps Polygon.
 */
function geoJsonToGooglePaths(
  geometry: GeoJsonPolygon | GeoJsonMultiPolygon
): google.maps.LatLngLiteral[][] {
  if (geometry.type === 'Polygon') {
    // Polygon has array of rings: [outer, ...holes]
    return geometry.coordinates.map(geoJsonRingToGooglePath);
  } else {
    // MultiPolygon has array of polygons, each with rings
    // Flatten to single array of paths
    return geometry.coordinates.flatMap((polygon: GeoJsonPosition[][]) =>
      polygon.map(geoJsonRingToGooglePath)
    );
  }
}

/**
 * Calculate polygon centroid for InfoWindow positioning.
 */
function getPolygonCentroid(
  paths: google.maps.LatLngLiteral[][],
  fallback: google.maps.LatLngLiteral
): google.maps.LatLngLiteral {
  // Use first path (outer ring) for centroid calculation
  const outerRing = paths[0] || [];
  if (outerRing.length === 0) {
    return fallback;
  }

  let sumLat = 0;
  let sumLng = 0;
  for (const point of outerRing) {
    sumLat += point.lat;
    sumLng += point.lng;
  }

  return {
    lat: sumLat / outerRing.length,
    lng: sumLng / outerRing.length,
  };
}

/**
 * Format CAGR for display.
 */
function formatCAGR(cagr: number): string {
  return `${(cagr * 100).toFixed(1)}%`;
}

export function GoogleSubmarketLayer({
  onSubmarketClick,
  onSubmarketHover,
  highlightedSubmarket,
}: GoogleSubmarketLayerProps) {
  // Hovered submarket for InfoWindow
  const [hoveredSubmarket, setHoveredSubmarket] = useState<{
    props: SubmarketProperties;
    position: google.maps.LatLngLiteral;
  } | null>(null);

  // Use store values
  const scoreType = useSubmarketStore((state) => state.selectedScoreType);
  const market = useActiveMarket();
  const mapCenter = { lat: market.center_lat, lng: market.center_lng };

  // Fetch data from APIs
  const { data: boundaries, isLoading: boundariesLoading } = useSubmarketBoundaries();
  const { data: scoresData, isLoading: scoresLoading } = useSubmarketScores();

  // Build score lookup map
  const scoreMap = useMemo(() => {
    if (!scoresData?.submarkets) return new Map<string, SubmarketScore>();
    return new Map(
      scoresData.submarkets.map((s) => [String(s.submarket_id), s])
    );
  }, [scoresData]);

  // Build submarket features with scores
  const submarketFeatures = useMemo(() => {
    if (!boundaries) return [];

    return boundaries.map((b) => {
      const score = scoreMap.get(b.submarket_id);
      const scoreValue = score ? getScoreValue(score, scoreType) : 50;
      const color = getScoreColor(scoreValue);
      const paths = geoJsonToGooglePaths(b.geometry);
      const centroid = getPolygonCentroid(paths, mapCenter);

      const props: SubmarketProperties = {
        submarket_id: b.submarket_id,
        name: b.name,
        cagr: b.properties.cagr_5yr,
        rank: b.properties.rank,
        score: scoreValue,
        composite_score: score?.composite_score ?? 0,
        rent_gap_score: score?.rent_gap_score ?? 0,
        mobi_score: score?.mobi_score ?? 0,
        momentum_score: score?.momentum_score ?? 0,
        vacancy_score: score?.vacancy_score ?? 0,
        valuation_score: score?.valuation_score ?? 0,
      };

      return {
        id: b.submarket_id,
        paths,
        centroid,
        color,
        props,
      };
    });
  }, [boundaries, scoreMap, scoreType, mapCenter.lat, mapCenter.lng]);

  // Handle polygon hover
  const handleMouseOver = useCallback(
    (feature: (typeof submarketFeatures)[0]) => {
      setHoveredSubmarket({
        props: feature.props,
        position: feature.centroid,
      });
      onSubmarketHover?.(feature.props);
    },
    [onSubmarketHover]
  );

  const handleMouseOut = useCallback(() => {
    setHoveredSubmarket(null);
    onSubmarketHover?.(null);
  }, [onSubmarketHover]);

  // Handle polygon click
  const handleClick = useCallback(
    (feature: (typeof submarketFeatures)[0]) => {
      onSubmarketClick?.(feature.props);
    },
    [onSubmarketClick]
  );

  // Loading state
  if (boundariesLoading || scoresLoading) {
    return null;
  }

  return (
    <>
      {/* Render submarket polygons */}
      {submarketFeatures.map((feature) => {
        const isHighlighted = feature.id === highlightedSubmarket;
        const isHovered = hoveredSubmarket?.props.submarket_id === feature.id;

        return (
          <GooglePolygon
            key={feature.id}
            paths={feature.paths}
            options={{
              fillColor: isHighlighted ? '#3b82f6' : feature.color,
              fillOpacity: isHovered ? 0.5 : isHighlighted ? 0.6 : 0.35,
              strokeColor: isHovered || isHighlighted ? '#1f2937' : '#374151',
              strokeWeight: isHovered || isHighlighted ? 2.5 : 1,
              strokeOpacity: 0.7,
              clickable: true,
              zIndex: isHighlighted ? 2 : isHovered ? 1 : 0,
            }}
            onMouseOver={() => handleMouseOver(feature)}
            onMouseOut={handleMouseOut}
            onClick={() => handleClick(feature)}
          />
        );
      })}

      {/* Tooltip InfoWindow */}
      {hoveredSubmarket && (
        <InfoWindow
          position={hoveredSubmarket.position}
          options={{
            disableAutoPan: true,
            pixelOffset: new google.maps.Size(0, -5),
          }}
          onCloseClick={() => setHoveredSubmarket(null)}
        >
          <div style={{ padding: '8px', fontFamily: 'system-ui, sans-serif', minWidth: '150px' }}>
            <div style={{ fontWeight: 600, fontSize: '13px', color: '#111827' }}>
              {hoveredSubmarket.props.name}
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                {getScoreLabel(scoreType)}:{' '}
                <span style={{ fontWeight: 600, color: getScoreColor(hoveredSubmarket.props.score) }}>
                  {hoveredSubmarket.props.score.toFixed(0)}
                </span>
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                CAGR:{' '}
                <span style={{ fontWeight: 600 }}>
                  {formatCAGR(hoveredSubmarket.props.cagr)}
                </span>
              </div>
            </div>
            <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>
              Rank: #{hoveredSubmarket.props.rank}
            </div>
          </div>
        </InfoWindow>
      )}
    </>
  );
}

export default GoogleSubmarketLayer;
